<template>
  <VueDraggable
    v-if="draggableEnabled"
    v-model="localIds"
    :class="dropZoneClass"
    :data-player-layout="grid ? 'grid' : 'list'"
    v-bind="dndOptions"
    @add="onDragAdd"
    @end="onDragEnd"
  >
    <PlayerCard
      v-for="player in playersInZone"
      :key="player.id"
      :player="player"
      :mode="viewMode"
      :board-mode="boardMode"
      :dimmed-by-filter="playerIsDimmed(player)"
      @use-build="(id, bi) => emit('use-build', id, bi)"
      @add-build="(id) => emit('add-build', id)"
      @use-guild-build="(id, buildId) => emit('use-guild-build', id, buildId)"
      @add-guild-build="emit('add-guild-build')"
      @edit-guild-build="(id, buildId, slot) => emit('edit-guild-build', id, buildId, slot)"
    />
    <div
      v-if="playersInZone.length === 0"
      class="pointer-events-none flex min-h-10 items-center justify-center rounded-md border border-dashed border-border-strong px-2 py-1.5 text-center text-xs uppercase tracking-[0.04em] text-text-muted select-none"
      :class="{ 'col-span-full': grid }"
      aria-hidden="true"
    >
      {{ messages.dropZone.dropHere }}
    </div>
  </VueDraggable>
  <div
    v-else
    :class="dropZoneClass"
    :data-player-layout="grid ? 'grid' : 'list'"
  >
    <PlayerCard
      v-for="player in playersInZone"
      :key="player.id"
      :player="player"
      :mode="viewMode"
      :board-mode="boardMode"
      :dimmed-by-filter="playerIsDimmed(player)"
      @use-build="(id, bi) => emit('use-build', id, bi)"
      @add-build="(id) => emit('add-build', id)"
      @use-guild-build="(id, buildId) => emit('use-guild-build', id, buildId)"
      @add-guild-build="emit('add-guild-build')"
      @edit-guild-build="(id, buildId, slot) => emit('edit-guild-build', id, buildId, slot)"
    />
    <div
      v-if="playersInZone.length === 0"
      class="pointer-events-none flex min-h-10 items-center justify-center rounded-md border border-dashed border-border-strong px-2 py-1.5 text-center text-xs uppercase tracking-[0.04em] text-text-muted select-none"
      :class="{ 'col-span-full': grid }"
      aria-live="polite"
    >
      {{ filterHidesPlayers ? messages.dropZone.noMatching : messages.dropZone.noPlayers }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
import type { DraggableEvent } from 'vue-draggable-plus'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { dropZoneOptions } from '@/shared/composables/useDragAndDrop'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import PlayerCard from './PlayerCard.vue'
import { PlayerFilterMode } from '../../lib/playerFilters'
import type { PlayerFilterMode as PlayerFilterModeType } from '../../lib/playerFilters'
import type { Player, PlayerGroup } from '../../types'
import { BoardMode, ViewMode } from '../../types'

type ViewModeType = (typeof ViewMode)[keyof typeof ViewMode]

export interface PlayerDropZoneProps {
  readonly group: PlayerGroup
  readonly players: ReadonlyArray<Player>
  readonly enabled?: boolean
  readonly boardMode?: BoardMode
  readonly grid?: boolean
  readonly playerFiltersActive?: boolean
  readonly playerFilterMode?: PlayerFilterModeType
  readonly matchingPlayerIds?: ReadonlySet<string>
}

const props = withDefaults(defineProps<PlayerDropZoneProps>(), {
  enabled: true,
  boardMode: BoardMode.Live,
  grid: false,
  playerFiltersActive: false,
  playerFilterMode: PlayerFilterMode.Highlight,
  matchingPlayerIds: () => new Set<string>(),
})

const emit = defineEmits<{
  (event: 'drag-add', playerId: string, index: number): void
  (event: 'drag-end', orderedIds: ReadonlyArray<string>): void
  (event: 'use-build', playerId: string, buildIndex: number): void
  (event: 'add-build', playerId: string): void
  (event: 'use-guild-build', playerId: string, buildId: string): void
  (event: 'add-guild-build'): void
  (event: 'edit-guild-build', playerId: string, buildId: string, slot: 0 | 1): void
}>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
const viewMode = computed<ViewModeType>(() => store.viewMode)
const boardMode = computed<BoardMode>(() => props.boardMode)
const filterHidesPlayers = computed<boolean>(
  () => props.playerFiltersActive && props.playerFilterMode === PlayerFilterMode.Filter,
)
const highlightUnmatched = computed<boolean>(
  () => props.playerFiltersActive && props.playerFilterMode === PlayerFilterMode.Highlight,
)
const draggableEnabled = computed<boolean>(() => props.enabled && !filterHidesPlayers.value)
const dropZoneClass = computed<string>(() => {
  if (!props.grid) return 'flex min-h-10 flex-1 flex-col gap-1'
  return viewMode.value === ViewMode.Compact
    ? 'grid min-h-10 flex-1 grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
    : 'grid min-h-10 flex-1 grid-cols-1 gap-1 md:grid-cols-2 board:grid-cols-3'
})

/**
 * Local mirror of `props.group.playerIds`. SortableJS mutates this directly
 * during a drag (so the DOM order matches the v-model), and the watcher
 * reseeds it from the store when the server-driven order actually differs
 * from what SortableJS already laid out locally.
 *
 * The watcher MUST compare order before reseeding — unconditional reseed
 * breaks the second drag in a row. The optimistic update mutates
 * `groups.value` synchronously with SortableJS's own mutation, so the
 * incoming `playerIds` and the current `localIds` end up with the same
 * content in the same order. Reassigning `localIds` to a fresh array
 * reference (even with identical content) re-fires SortableJS's v-model
 * handling on an empty internal drag state — leaving the previously-
 * dragged card pinned with `dnd_chosen` / `dnd_drag` classes until the
 * next manual click clears them, and breaking subsequent drags because
 * SortableJS still holds a reference to the now-stale DOM node. Only
 * reseed when the order is actually different (server-side drift,
 * `boardChanged` state snapshot, etc.); the server snapshot is authoritative
 * for both local and remote mutations.
 *
 * The `v-for` MUST read `playersInZone` (which is derived from `localIds`),
 * NOT a store getter. SortableJS moves DOM nodes between drop zones and
 * relies on the v-for tracking the same array as `v-model` so Vue can
 * reconcile the moved vnode into its new position. If the v-for reads a
 * different array, Vue never follows the v-model mutation — the moved
 * card is removed from the DOM and never re-inserted, leaving a stale
 * duplicate in the destination's drop zone.
 */
const localIds = ref<string[]>([...props.group.playerIds])

watch(
  () => props.group.playerIds,
  (next) => {
    const current = localIds.value
    if (
      next.length === current.length
      && next.every((id, i) => id === current[i])
    ) {
      return
    }
    localIds.value = [...next]
  },
)

const playersById = computed<ReadonlyMap<string, Player>>(
  () => new Map(props.players.map((player) => [player.id, player])),
)

const allPlayersInZone = computed<ReadonlyArray<Player>>(() =>
  localIds.value
    .map((id) => playersById.value.get(id))
    .filter((player): player is Player => Boolean(player)),
)

const playersInZone = computed<ReadonlyArray<Player>>(() => {
  if (!filterHidesPlayers.value) return allPlayersInZone.value
  return allPlayersInZone.value.filter((player) => props.matchingPlayerIds?.has(player.id) ?? true)
})

const dndOptions = dropZoneOptions<string>()

function playerIsDimmed(player: Player): boolean {
  return highlightUnmatched.value && !(props.matchingPlayerIds?.has(player.id) ?? true)
}

function movedPlayerIdFromEvent(event: DraggableEvent<string>): string | undefined {
  return typeof event.data === 'string' && event.data !== '' ? event.data : undefined
}

function onDragAdd(event: DraggableEvent<string>): void {
  const playerId = movedPlayerIdFromEvent(event)
  if (!playerId) return
  const index = typeof event.newIndex === 'number' ? event.newIndex : Number.MAX_SAFE_INTEGER
  emit('drag-add', playerId, index)
}

function onDragEnd(event: DraggableEvent<string>): void {
  if (event.from !== event.to) return
  emit('drag-end', [...localIds.value])
}
</script>
